import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  Plus, Pencil, Trash2, Search, Bell,
  X, AlertTriangle, Check, Loader2
} from "lucide-react";

import "../styles/leavePoliciesPage.css";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api`;

// ═══════════════════════════════════════════
//  AUTH HEADER HELPER
// ═══════════════════════════════════════════
const authHeaders = (token) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

// ═══════════════════════════════════════════
//  API CALLS (token required)
// ═══════════════════════════════════════════
const fetchAccrualTypes = async (token) => {
  const res = await fetch(`${API_BASE}/dropdowns/values/Accrualtype`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error("Failed to fetch accrual types");
  return res.json();
};

const fetchEmployeeTypes = async (token) => {
  const res = await fetch(`${API_BASE}/dropdowns/values/EmployeeType`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error("Failed to fetch employee types");
  return res.json();
};

const fetchLeaveTypes = async (token) => {
  const res = await fetch(`${API_BASE}/leave-types`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error("Failed to fetch leave types");
  return res.json();
};

const fetchLeavePolicies = async (token) => {
  const res = await fetch(`${API_BASE}/leave-policies`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error("Failed to fetch policies");
  return res.json();
};

const createLeavePolicy = async (token, data) => {
  const res = await fetch(`${API_BASE}/leave-policies`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create policy");
  return res.json();
};

const updateLeavePolicy = async (token, id, data) => {
  const res = await fetch(`${API_BASE}/leave-policies/${id}`, {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update policy");
  return res.json();
};

const deleteLeavePolicy = async (token, id) => {
  const res = await fetch(`${API_BASE}/leave-policies/${id}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error("Failed to delete policy");
  return res.json();
};

// ═══════════════════════════════════════════
//  HELPERS & CONSTANTS
// ═══════════════════════════════════════════
const mapPolicy = (d) => ({
  id:              d.id,
  leaveTypeId:     d.leave_type_id,
  leaveType:       d.leave_type_name || "",
  leaveTypeCode:   d.leave_type_code || "",
  employeeType:    d.employee_type,
  allocationCycle: d.allocation_cycle,
  allocatedUnits:  parseFloat(d.allocated_days) || 0,
  effectiveFrom:   d.effective_from,
  effectiveTo:     d.effective_to,
});

const toDateInput = (iso) =>
  iso ? new Date(iso).toISOString().split("T")[0] : "";

const EMPTY_FORM = {
  employeeType:    "",
  leaveTypeId:     "",
  allocationCycle: "",
  allocatedUnits:  "",
  effectiveFrom:   "",
  effectiveTo:     "",
};

const BADGE_COLORS = {
  "EMPLOYEE":  "badge-fulltime",
  "Full-time": "badge-fulltime",
  "Contract":  "badge-contract",
  "Intern":    "badge-intern",
};

const CYCLE_COLORS = {
  "YEARLY":  "cycle-annual",
  "MONTHLY": "cycle-monthly",
  "ONE_TIME":"cycle-quarterly",
  "NONE":    "cycle-quarterly",
};

// ═══════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════
export default function LeavePoliciesPage({ isTab = false }) {
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [policies,      setPolicies]      = useState([]);
  const [leaveTypes,    setLeaveTypes]    = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [accrualTypes,  setAccrualTypes]  = useState([]);
  const [isOpen,        setIsOpen]        = useState(false);
  const [editItem,      setEditItem]      = useState(null);
  const [deleteId,      setDeleteId]      = useState(null);
  const [formData,      setFormData]      = useState(EMPTY_FORM);
  const [errors,        setErrors]        = useState({});
  const [search,        setSearch]        = useState("");
  const [loading,       setLoading]       = useState(true);
  const [submitting,    setSubmitting]    = useState(false);
  const [deleting,      setDeleting]      = useState(false);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // ── Token from AuthContext ──
  const { token } = useAuth();

  // ── Load all data on mount ──
  useEffect(() => {
    if (!token) return; 
    setLoading(true);
    Promise.all([
      fetchEmployeeTypes(token),
      fetchAccrualTypes(token),
      fetchLeaveTypes(token),
      fetchLeavePolicies(token),
    ])
      .then(([empTypes, accTypes, ltypes, rawPolicies]) => {
        setEmployeeTypes(empTypes);
        setAccrualTypes(accTypes);
        setLeaveTypes(ltypes);
        setPolicies(rawPolicies.map(mapPolicy));
      })
      .catch(() => showToast("Failed to load data", "error"))
      .finally(() => setLoading(false));
  }, [token]); 

  const reloadPolicies = async () => {
    try {
      const rawPolicies = await fetchLeavePolicies(token);
      setPolicies(rawPolicies.map(mapPolicy));
    } catch (e) {
      console.error("Failed to reload policies", e);
    }
  };

  const showToast = (msg, type = "success") => {
    if (type === "error") {
      toast.error(msg);
    } else {
      toast.success(msg);
    }
  };

  const openCreate = () => {
    setFormData({
      ...EMPTY_FORM,
      employeeType:    employeeTypes[0]?.dropdown_value_code || "",
      allocationCycle: accrualTypes[0]?.dropdown_value_code  || "",
      leaveTypeId:     leaveTypes[0]?.id                     || "",
    });
    setEditItem(null);
    setErrors({});
    setIsOpen(true);
  };

  const openEdit = (item) => {
    setFormData({
      employeeType:    item.employeeType,
      leaveTypeId:     item.leaveTypeId,
      allocationCycle: item.allocationCycle,
      allocatedUnits:  item.allocatedUnits,
      effectiveFrom:   toDateInput(item.effectiveFrom),
      effectiveTo:     toDateInput(item.effectiveTo),
    });
    setEditItem(item);
    setErrors({});
    setIsOpen(true);
  };

  const validate = () => {
    const e = {};
    if (!formData.employeeType)    e.employeeType    = "Required";
    if (!formData.leaveTypeId)     e.leaveTypeId     = "Required";
    if (!formData.allocationCycle) e.allocationCycle = "Required";
    if (!formData.allocatedUnits)  e.allocatedUnits  = "Required";
    if (!formData.effectiveFrom)   e.effectiveFrom   = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => {
    const selectedLeaveType = leaveTypes.find(
      (lt) => lt.id === Number(formData.leaveTypeId)
    );
    const isHourBased = selectedLeaveType?.unit_type === "HOUR" || selectedLeaveType?.unitType === "HOUR";

    return {
      leaveTypeId:     Number(formData.leaveTypeId),
      leave_type_id:   Number(formData.leaveTypeId),
      employeeType:    formData.employeeType,
      employee_type:   formData.employeeType,
      allocationCycle: formData.allocationCycle,
      allocation_cycle: formData.allocationCycle,
      allocatedDays:   isHourBased ? null : Number(formData.allocatedUnits),
      allocated_days:  isHourBased ? null : Number(formData.allocatedUnits),
      allocatedHours:  isHourBased ? Number(formData.allocatedUnits) : null,
      allocated_hours: isHourBased ? Number(formData.allocatedUnits) : null,
      effectiveFrom:   formData.effectiveFrom,
      effective_from:  formData.effectiveFrom,
      effectiveTo:     formData.effectiveTo || null,
      effective_to:    formData.effectiveTo || null,
    };
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (editItem) {
        await updateLeavePolicy(token, editItem.id, buildPayload());
        showToast("Policy updated successfully!");
      } else {
        await createLeavePolicy(token, buildPayload());
        showToast("Policy created successfully!");
      }
      await reloadPolicies();
      setIsOpen(false);
    } catch {
      showToast("Something went wrong!", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteLeavePolicy(token, deleteId);
      setPolicies((prev) => prev.filter((p) => p.id !== deleteId));
      setDeleteId(null);
      showToast("Policy deleted successfully!");
      await reloadPolicies();
    } catch {
      showToast("Delete failed!", "error");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = policies.filter(
    (p) =>
      p.employeeType.toLowerCase().includes(search.toLowerCase()) ||
      p.leaveType.toLowerCase().includes(search.toLowerCase())
  );

  // code → display name for table
  const getEmployeeLabel = (code) =>
    employeeTypes.find((e) => e.dropdown_value_code === code)?.dropdown_value_name || code;

  const getCycleLabel = (code) =>
    accrualTypes.find((a) => a.dropdown_value_code === code)?.dropdown_value_name || code;

  const set = (key, val) => setFormData((f) => ({ ...f, [key]: val }));

  // ═══════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════
  const pageContent = (
    <>
    <div className="lp-wrap" style={isTab ? { padding: 0 } : {}}>
      {!isTab && (
        <header className="lp-topbar">
          <div className="lp-topbar-left">
            <h1>Leave Policies</h1>
            <p>Manage allocation policies per employee type</p>
          </div>
          <div className="lp-topbar-right">
            <div className="lp-search-wrap">
              <Search size={14} className="lp-search-icon" />
              <input
                className="lp-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
              />
            </div>
            <div className="lp-bell">
              <Bell size={20} />
              <span className="lp-bell-badge">3</span>
            </div>
          </div>
        </header>
      )}

      <main className="lp-content" style={isTab ? { padding: 0, marginTop: 0 } : {}}>
              <div className="lp-section-header">
                <div>
                  <h2>All Policies</h2>
                  <p>{policies.length} policies configured</p>
                </div>
                <button className="lp-add-btn" onClick={openCreate}>
                  <Plus size={15} /> Add Policy
                </button>
              </div>

              <div className="lp-table-card">
                {loading ? (
                  <div style={{ padding: "3rem", textAlign: "center", color: "#888", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />
                    Loading policies...
                  </div>
                ) : (
                  <table className="lp-table">
                    <thead>
                      <tr>
                        {["Employee Type","Leave Type","Cycle","Allocated Days","Effective From","Effective To","Actions"].map((col) => (
                          <th key={col}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="lp-empty">No policies found.</td>
                        </tr>
                      ) : (
                        filtered.map((p) => (
                          <tr key={p.id}>
                            <td>
                              <span className={`lp-badge ${BADGE_COLORS[p.employeeType] || "badge-default"}`}>
                                {getEmployeeLabel(p.employeeType)}
                              </span>
                            </td>
                            <td className="col-primary">
                              {p.leaveType}
                              {p.leaveTypeCode && (
                                <span style={{ fontSize: "11px", color: "#999", marginLeft: "6px" }}>
                                  ({p.leaveTypeCode})
                                </span>
                              )}
                            </td>
                            <td>
                              <span className={`lp-cycle ${CYCLE_COLORS[p.allocationCycle] || ""}`}>
                                {getCycleLabel(p.allocationCycle)}
                              </span>
                            </td>
                            <td className="col-number">{p.allocatedUnits}</td>
                            <td className="col-muted">
                              {p.effectiveFrom
                                ? format(new Date(p.effectiveFrom), "MMM dd, yyyy")
                                : "-"}
                            </td>
                            <td className="col-muted">
                              {p.effectiveTo
                                ? format(new Date(p.effectiveTo), "MMM dd, yyyy")
                                : "-"}
                            </td>
                            <td>
                              <div className="lp-actions">
                                <button className="lp-icon-btn edit" onClick={() => openEdit(p)} title="Edit">
                                  <Pencil size={14} />
                                </button>
                                <button className="lp-icon-btn del" onClick={() => setDeleteId(p.id)} title="Delete">
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </main>
          </div>

          {/* ── Add / Edit Modal ── */}
          {isOpen && (
            <div className="lp-overlay">
              <div className="lp-modal">
                <div className="lp-modal-header">
                  <div>
                    <h3>{editItem ? "Edit Leave Policy" : "Add Leave Policy"}</h3>
                    <p>{editItem ? "Update policy details" : "Create a new allocation policy"}</p>
                  </div>
                  <button className="lp-modal-close" onClick={() => setIsOpen(false)}>
                    <X size={18} />
                  </button>
                </div>

                <div className="lp-modal-body">

                  {/* Employee Type */}
                  <div className="lp-form-group">
                    <label>Employee Type <span>*</span></label>
                    <select
                      className={`lp-input ${errors.employeeType ? "has-error" : ""}`}
                      value={formData.employeeType}
                      onChange={(e) => set("employeeType", e.target.value)}
                    >
                      <option value="">-- Select Employee Type --</option>
                      {employeeTypes.map((et) => (
                        <option key={et.dropdown_value_code} value={et.dropdown_value_code}>
                          {et.dropdown_value_name}
                        </option>
                      ))}
                    </select>
                    {errors.employeeType && <p className="lp-error">{errors.employeeType}</p>}
                  </div>

                  {/* Leave Type */}
                  <div className="lp-form-group">
                    <label>Leave Type <span>*</span></label>
                    <select
                      className={`lp-input ${errors.leaveTypeId ? "has-error" : ""}`}
                      value={formData.leaveTypeId}
                      onChange={(e) => set("leaveTypeId", e.target.value)}
                    >
                      <option value="">-- Select Leave Type --</option>
                      {leaveTypes
                        .filter((lt) => lt.is_active !== false)
                        .map((lt) => (
                          <option key={lt.id} value={lt.id}>
                            {lt.name} {lt.code ? `(${lt.code})` : ""}
                          </option>
                        ))}
                    </select>
                    {errors.leaveTypeId && <p className="lp-error">{errors.leaveTypeId}</p>}
                  </div>

                  {/* Allocation Cycle */}
                  <div className="lp-form-group">
                    <label>Allocation Cycle <span>*</span></label>
                    <select
                      className={`lp-input ${errors.allocationCycle ? "has-error" : ""}`}
                      value={formData.allocationCycle}
                      onChange={(e) => set("allocationCycle", e.target.value)}
                    >
                      <option value="">-- Select Cycle --</option>
                      {accrualTypes.map((at) => (
                        <option key={at.dropdown_value_code} value={at.dropdown_value_code}>
                          {at.dropdown_value_name}
                        </option>
                      ))}
                    </select>
                    {errors.allocationCycle && <p className="lp-error">{errors.allocationCycle}</p>}
                  </div>

                  {/* Allocated Days */}
                  <div className="lp-form-group">
                    <label>Allocated Days <span>*</span></label>
                    <input
                      type="number"
                      min="0"
                      className={`lp-input ${errors.allocatedUnits ? "has-error" : ""}`}
                      value={formData.allocatedUnits}
                      onChange={(e) => set("allocatedUnits", e.target.value)}
                      placeholder="e.g. 12"
                    />
                    {errors.allocatedUnits && <p className="lp-error">{errors.allocatedUnits}</p>}
                  </div>

                  {/* Effective From / To */}
                  <div className="lp-form-row">
                    <div className="lp-form-group">
                      <label>Effective From <span>*</span></label>
                      <input
                        type="date"
                        className={`lp-input ${errors.effectiveFrom ? "has-error" : ""}`}
                        value={formData.effectiveFrom}
                        onChange={(e) => set("effectiveFrom", e.target.value)}
                      />
                      {errors.effectiveFrom && <p className="lp-error">{errors.effectiveFrom}</p>}
                    </div>
                    <div className="lp-form-group">
                      <label>
                        Effective To{" "}
                        <span style={{ color: "#aaa", fontWeight: 400 }}>(optional)</span>
                      </label>
                      <input
                        type="date"
                        className="lp-input"
                        value={formData.effectiveTo}
                        onChange={(e) => set("effectiveTo", e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="lp-modal-footer">
                  <button className="btn-cancel" onClick={() => setIsOpen(false)} disabled={submitting}>
                    Cancel
                  </button>
                  <button className="btn-primary" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? (
                      <>
                        <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
                        {editItem ? "Saving..." : "Creating..."}
                      </>
                    ) : (
                      editItem ? "Save Changes" : "Create Policy"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Delete Confirm Modal ── */}
          {deleteId && (
            <div className="lp-overlay">
              <div className="lp-delete-modal">
                <div className="lp-delete-icon">
                  <AlertTriangle size={24} />
                </div>
                <h3>Delete Policy?</h3>
                <p>This action cannot be undone.</p>
                <div className="lp-delete-actions">
                  <button className="btn-cancel" onClick={() => setDeleteId(null)} disabled={deleting}>
                    Cancel
                  </button>
                  <button className="btn-danger" onClick={handleDelete} disabled={deleting}>
                    {deleting ? (
                      <>
                        <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
                        Deleting...
                      </>
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
    </>
  );

  if (isTab) return pageContent;

  return (
    <>
      <div className="page container">
        {sidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}
        <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <main className="main">
          <div className="hamburger" onClick={toggleSidebar}>☰</div>
          {pageContent}
          <Footer />
        </main>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}